import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export const INPUT_DISCARD_REASONS = [
  { value: "Expired", label: "Expired" },
  { value: "Damaged", label: "Damaged" },
  { value: "PoorQuality", label: "Poor Quality" },
  { value: "Contaminated", label: "Contaminated" },
  { value: "Other", label: "Other" },
] as const;

export interface CreateInputDiscardRequestDto {
  // Provide exactly one of inputBatchId or seedBatchId
  inputBatchId?: string;
  seedBatchId?: string;
  quantityToDiscard: number;
  reason: string;
  reasonDetails: string;
  requiresApproval?: boolean;
}

export interface ApproveInputDiscardRequestDto {
  discardRequestId: string;
  isApproved: boolean;
  approvalNotes?: string;
}

export interface InputDiscardRequestDto {
  id: string;
  // One of these will be set:
  inputBatchId?: string;
  seedBatchId?: string;
  seedBatchNumber?: string;
  customerName?: string;
  inputName: string;   // crop/input name
  batchNumber: string; // inputBatch or seedBatch batch number
  quantityToDiscard: number;
  unit: string;
  reason: string;
  reasonDetails: string;
  status: string;
  requiresApproval: boolean;
  approvedByUserId?: string;
  approvedByUserName?: string;
  approvedAt?: string;
  approvalNotes?: string;
  requestedByUserId: string;
  requestedByUserName: string;
  requestedAt: string;
  processedAt?: string;
}

export const inputDiscardApi = {
  create: async (dto: CreateInputDiscardRequestDto): Promise<InputDiscardRequestDto> => {
    const { data } = await apiClient.post<ApiResponse<InputDiscardRequestDto>>(
      "/inputdiscard",
      dto
    );
    return data.data;
  },

  getPending: async (): Promise<InputDiscardRequestDto[]> => {
    const { data } = await apiClient.get<ApiResponse<InputDiscardRequestDto[]>>(
      "/inputdiscard/pending"
    );
    return data.data;
  },

  getApproved: async (): Promise<InputDiscardRequestDto[]> => {
    const { data } = await apiClient.get<ApiResponse<InputDiscardRequestDto[]>>(
      "/inputdiscard/approved"
    );
    return data.data;
  },

  approve: async (
    id: string,
    dto: ApproveInputDiscardRequestDto
  ): Promise<InputDiscardRequestDto> => {
    const { data } = await apiClient.post<ApiResponse<InputDiscardRequestDto>>(
      `/inputdiscard/${id}/approve`,
      dto
    );
    return data.data;
  },

  process: async (id: string): Promise<InputDiscardRequestDto> => {
    const { data } = await apiClient.post<ApiResponse<InputDiscardRequestDto>>(
      `/inputdiscard/${id}/process`
    );
    return data.data;
  },

  approveAndWriteOff: async (
    id: string,
    approvalNotes?: string
  ): Promise<InputDiscardRequestDto> => {
    const { data } = await apiClient.post<ApiResponse<InputDiscardRequestDto>>(
      `/inputdiscard/${id}/approve-and-write-off`,
      { approvalNotes: approvalNotes ?? null }
    );
    return data.data;
  },
};
